<script lang="ts" setup>
import { cva, type VariantProps } from 'class-variance-authority'

import { classMerge } from '../utils'

const buttonVariants = cva('inline-flex justify-center items-center font-button focus-visible:outline-none focus-visible:ring focus-visible:ring-offset-1 ring-ring', {
  variants: {
    variant: {
      primary: 'bg-action text-action-foreground border-2 border-action-stroke rounded-action-primary hover:bg-action-hover hover:text-action-hover-foreground hover:border-action-hover-stroke focus:bg-action-focus focus:text-action-focus-foreground focus:border-action-focus-stroke',
      secondary: 'bg-action-secondary-background text-action-secondary-foreground border-2 border-action-secondary-stroke rounded-action-secondary hover:text-action-secondary-hover-foreground hover:border-action-secondary-hover-stroke focus:bg-action-secondary-focus focus:border-action-secondary-focus-stroke focus:text-action-secondary-focus-foreground hover:bg-action-secondary-hover-background',
      ghost: 'text-action-ghost-foreground rounded-action-ghost hover:text-action-ghost-hover-foreground focus:text-action-ghost-hover-foreground',
      link: 'text-action-link-foreground border-b-2 border-action-link-stroke pb-0.5 hover:text-action-link-hover-foreground hover:border-action-link-hover-stroke'
    },
    size: {
      sm: 'text-button-xs gap-1',
      md: 'text-button-sm gap-1',
      lg: 'text-button-md gap-1.5',
      xl: 'text-button-lg gap-1.5'
    },
    type: {
      regular: '',
      'icon-only': ''
    },
    disabled: {
      true: 'pointer-events-none',
      false: ''
    },
    useDisabledOpacity: {
      true: '',
      false: ''
    },
    inverted: {
      true: 'inverted',
      false: 'normal'
    }
  },
  compoundVariants: [
    {
      disabled: true,
      useDisabledOpacity: true,
      class: 'opacity-50'
    },
    {
      variant: 'primary',
      size: 'sm',
      type: 'regular',
      class: 'h-action-sm px-action-sm-x'
    },
    {
      variant: 'primary',
      size: 'sm',
      type: 'icon-only',
      class: 'h-action-sm w-action-sm'
    },
    {
      variant: 'secondary',
      size: 'sm',
      type: 'regular',
      class: 'h-action-sm px-action-sm-x'
    },
    {
      variant: 'secondary',
      size: 'sm',
      type: 'icon-only',
      class: 'h-action-sm w-action-sm'
    },
    {
      variant: 'ghost',
      size: 'sm',
      type: 'regular',
      class: 'h-action-sm px-action-sm-x'
    },
    {
      variant: 'ghost',
      size: 'sm',
      type: 'icon-only',
      class: 'h-action-sm w-action-sm'
    },
    {
      variant: 'primary',
      size: 'md',
      type: 'regular',
      class: 'h-action-md px-action-md-x'
    },
    {
      variant: 'primary',
      size: 'md',
      type: 'icon-only',
      class: 'h-action-md w-action-md'
    },
    {
      variant: 'secondary',
      size: 'md',
      type: 'regular',
      class: 'h-action-md px-action-md-x'
    },
    {
      variant: 'secondary',
      size: 'md',
      type: 'icon-only',
      class: 'h-action-md w-action-md'
    },
    {
      variant: 'ghost',
      size: 'md',
      type: 'regular',
      class: 'h-action-md px-action-md-x'
    },
    {
      variant: 'ghost',
      size: 'md',
      type: 'icon-only',
      class: 'h-action-md w-action-md'
    },
    {
      variant: 'primary',
      size: 'lg',
      type: 'regular',
      class: 'h-action-lg px-action-lg-x'
    },
    {
      variant: 'primary',
      size: 'lg',
      type: 'icon-only',
      class: 'h-action-lg w-action-lg'
    },
    {
      variant: 'secondary',
      size: 'lg',
      type: 'regular',
      class: 'h-action-lg px-action-lg-x'
    },
    {
      variant: 'secondary',
      size: 'lg',
      type: 'icon-only',
      class: 'h-action-lg w-action-lg'
    },
    {
      variant: 'ghost',
      size: 'lg',
      type: 'regular',
      class: 'h-action-lg px-action-lg-x'
    },
    {
      variant: 'ghost',
      size: 'lg',
      type: 'icon-only',
      class: 'h-action-lg w-action-lg'
    },
    {
      variant: 'primary',
      size: 'xl',
      type: 'regular',
      class: 'h-action-xl px-action-xl-x'
    },
    {
      variant: 'primary',
      size: 'xl',
      type: 'icon-only',
      class: 'h-action-xl w-action-xl'
    },
    {
      variant: 'secondary',
      size: 'xl',
      type: 'regular',
      class: 'h-action-xl px-action-xl-x'
    },
    {
      variant: 'secondary',
      size: 'xl',
      type: 'icon-only',
      class: 'h-action-xl w-action-xl'
    },
    {
      variant: 'ghost',
      size: 'xl',
      type: 'regular',
      class: 'h-action-xl px-action-xl-x'
    },
    {
      variant: 'ghost',
      size: 'xl',
      type: 'icon-only',
      class: 'h-action-xl w-action-xl'
    }
  ],
  defaultVariants: {
    variant: 'primary',
    size: 'lg',
    type: 'regular'
  }
})

type ButtonProps = VariantProps<typeof buttonVariants>

const props = withDefaults(
  defineProps<{
    variant?: ButtonProps['variant'],
    size?: ButtonProps['size'],
    type?: ButtonProps['type'],
    disabled?: boolean,
    useDisabledOpacity?: ButtonProps['useDisabledOpacity'],
    // eslint-disable-next-line vue/no-unused-properties
    href?: string,
    // eslint-disable-next-line vue/no-unused-properties
    target?: string,
    inverted?: boolean
  }>(),
  {
    variant: 'primary',
    size: 'lg',
    type: 'regular',
    disabled: false,
    useDisabledOpacity: true,
    href: '',
    target: '',
    inverted: false
  }
)

const emit = defineEmits<{
  click: [id: number]
}>()

const onClick = (event: Event) => {
  if (!props.disabled) {
    emit('click', 1)
    const buttonElement = event.currentTarget as HTMLElement
    if (buttonElement) {
      buttonElement.blur()
    }
  }
}
</script>

<template>
  <component
    :is="props.href ? 'a' : 'button'"
    :class="classMerge(buttonVariants({ variant, size, type, disabled, useDisabledOpacity, inverted }), $attrs.class ?? '')"
    :href="props.href || null"
    :target="props.href ? props.target : null"
    :disabled="!props.href && props.disabled"
    @click="onClick">
    <slot />
  </component>
</template>
